import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { checkAndConvertSetsInObject, checkAndRevertObjectToSet } from '../convertSetsInObject';

import { getItem, setItem } from '../storage';
import { deepEqualUnordered } from '../checkIfIsEqual';

const SET_TRANSFORMED_KEY = '__SET_TRANSFORMED__';
export enum LocalStorageFiltersKeys {
  documents = 'Documents',
  // [TODO] try to find a way to have both filters and sorting saved under the same key "documents"
  documentsSorting = 'DocumentsSorting',
  checklists = 'Checklists',
  groups = 'Groups',
  news = 'News',
  alarmsLis = 'alarms',
}

const getStorageFilters = <T,>(key: string, defaultValue: T): T => {
  // getting stored value
  const savedAppFilters = getItem('APP_FILTERS');
  if (!savedAppFilters) return defaultValue;
  const savedFilters = savedAppFilters[key];
  if (!savedFilters) return defaultValue;
  const initial = JSON.parse(savedFilters);
  return checkAndRevertObjectToSet(initial, SET_TRANSFORMED_KEY) || defaultValue;
};

export const useLocalStorageFilters = <T extends { [index: string]: any }>(
  key: LocalStorageFiltersKeys,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] => {
  const [filters, setFilter] = useState<T>(() => {
    return getStorageFilters(key, defaultValue);
  });

  useEffect(() => {
    // storing input name
    setItem('APP_FILTERS', {
      ...getItem('APP_FILTERS'),
      [key]: JSON.stringify(checkAndConvertSetsInObject(filters, SET_TRANSFORMED_KEY)),
    });
  }, [key, filters]);

  return [filters, setFilter];
};

type CheckIfFiltersAppliedParams<T> = {
  defaultFilters: T;
  filters: T;
};
export const checkIfFiltersApplied = <T extends { [index: string]: any }>(
  params: CheckIfFiltersAppliedParams<T>
): boolean => {
  const { defaultFilters, filters } = params;

  return !deepEqualUnordered(defaultFilters, filters);
};
